import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { preOrderService } from '../../services/preOrderService';
import type { PreOrder } from '../../types/catalog';
import { PRE_ORDER_STATUS_COLORS, PRE_ORDER_STATUS_LABELS } from './preOrderStatus';
import { formatDateTime } from '../../utils/dateTimeUtils';

const formatPrice = (value: number): string => `$${value.toFixed(2)}`;

/** Client-side read-only view of one of the current user's pre-orders. */
export default function MyPreOrderDetailPage() {
  const { preOrderId } = useParams<{ preOrderId: string }>();
  const navigate = useNavigate();

  const [preOrder, setPreOrder] = useState<PreOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setPreOrder(await preOrderService.getMine(parseInt(preOrderId ?? '')));
      } catch (e) {
        console.error('Error fetching my pre-order:', e);
        setError('Не вдалося завантажити передзамовлення');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [preOrderId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 800, mx: 'auto', width: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/my-preorders')}
          size="small"
        >
          До моїх передзамовлень
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          {preOrder ? `Передзамовлення №${preOrder.id}` : 'Передзамовлення'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {preOrder && (
        <>
          <Box sx={{ mb: 1 }}>
            <Chip
              label={PRE_ORDER_STATUS_LABELS[preOrder.status]}
              color={PRE_ORDER_STATUS_COLORS[preOrder.status]}
            />
          </Box>

          <Typography variant="body1" color="text.secondary">
            Каталог: {preOrder.catalogName}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Створено: {formatDateTime(preOrder.createdDate)}
          </Typography>

          <List>
            {preOrder.items.map((item) => (
              <ListItem key={item.productId} divider>
                <ListItemText
                  primary={item.productName}
                  secondary={`${item.quantity} × ${formatPrice(item.unitPrice)}`}
                />
                <Typography variant="body1">{formatPrice(item.quantity * item.unitPrice)}</Typography>
              </ListItem>
            ))}
          </List>
          <Typography variant="h6" sx={{ textAlign: 'right', mt: 1, mb: 3 }}>
            Разом: {formatPrice(preOrder.total)}
          </Typography>

          <Button variant="outlined" onClick={() => navigate(`/catalogs/${preOrder.catalogId}/view`)}>
            Перейти до каталогу
          </Button>
        </>
      )}
    </Box>
  );
}
